import { c as _c } from "react/compiler-runtime";
import { IcoChevronRight16, type Icon } from '@onefootprint/icons';
import * as HoverCard from '@radix-ui/react-hover-card';
import { cx } from 'class-variance-authority';
import { AnimatePresence, motion } from 'framer-motion';
import Link from 'next/link';
import { memo, useEffect, useRef, useState } from 'react';
import SubLink from './components/sub-link';
export type MenuLink = {
  href: string;
  text: string;
  suffixElement?: React.ReactNode;
};
export type MenuDropdownProps = {
  text: string;
  icon: Icon;
  isCollapsed: boolean;
  subLinks: MenuLink[];
  pathname: string;
  annotationBadge?: React.ReactNode;
  suffixElement?: React.ReactNode;
};
const dropdownAnimation = {
  initial: {
    opacity: 0,
    height: 0
  },
  animate: {
    opacity: 1,
    height: 'auto'
  },
  exit: {
    opacity: 0,
    height: 0
  },
  transition: {
    duration: 0.1
  }
};
const isPathUnder = (href: string, pathname: string) => pathname === href || pathname.startsWith(`${href}/`);
const MenuDropdown = t0 => {
  const $ = _c(67);
  const {
    text,
    icon: Icon,
    isCollapsed,
    subLinks,
    pathname,
    annotationBadge,
    suffixElement
  } = t0;
  let t1;
  if ($[0] !== pathname || $[1] !== subLinks) {
    let t2;
    if ($[3] !== pathname) {
      t2 = s => isPathUnder(s.href, pathname);
      $[3] = pathname;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = subLinks.filter(t2).reduce(_temp, null);
    $[0] = pathname;
    $[1] = subLinks;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const activeHref = t1;
  const menuActive = activeHref !== null;
  const [isOpen, setIsOpen] = useState(menuActive);
  const triggerRef = useRef(null);
  let t2;
  if ($[5] !== isCollapsed || $[6] !== menuActive) {
    t2 = () => {
      if (menuActive && !isCollapsed) {
        setIsOpen(true);
      }
    };
    $[5] = isCollapsed;
    $[6] = menuActive;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== menuActive) {
    t3 = [menuActive];
    $[8] = menuActive;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[10] !== isCollapsed) {
    t4 = () => {
      if (isCollapsed) {
        setIsOpen(false);
      }
    };
    t5 = [isCollapsed];
    $[10] = isCollapsed;
    $[11] = t4;
    $[12] = t5;
  } else {
    t4 = $[11];
    t5 = $[12];
  }
  useEffect(t4, t5);
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setIsOpen(_temp2);
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleToggle = t6;
  if (isCollapsed) {
    const t7 = subLinks[0].href;
    const t8 = menuActive ? "text-primary bg-senary/70" : "text-tertiary hover:text-primary hover:bg-hover";
    let t9;
    if ($[14] !== t8) {
      t9 = cx("flex size-7 mx-auto items-center justify-center rounded-default cursor-pointer", "transition-transform duration-fast ease-out active:scale-pressed motion-reduce:active:scale-100", t8);
      $[14] = t8;
      $[15] = t9;
    } else {
      t9 = $[15];
    }
    let t10;
    if ($[16] !== Icon) {
      t10 = <Icon />;
      $[16] = Icon;
      $[17] = t10;
    } else {
      t10 = $[17];
    }
    let t11;
    if ($[18] !== subLinks[0].href || $[19] !== t10 || $[20] !== t9 || $[21] !== text) {
      t11 = <HoverCard.Trigger asChild={true}><Link ref={triggerRef} href={t7} aria-label={text} className={t9}>{t10}</Link></HoverCard.Trigger>;
      $[18] = subLinks[0].href;
      $[19] = t10;
      $[20] = t9;
      $[21] = text;
      $[22] = t11;
    } else {
      t11 = $[22];
    }
    let t12;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = event => {
        if (triggerRef.current?.contains(event.target as Node)) {
          event.preventDefault();
        }
      };
      $[23] = t12;
    } else {
      t12 = $[23];
    }
    let t13;
    if ($[24] !== text) {
      t13 = <div className="px-2 py-1 text-label-3 text-primary">{text}</div>;
      $[24] = text;
      $[25] = t13;
    } else {
      t13 = $[25];
    }
    let t14;
    if ($[26] !== activeHref || $[27] !== subLinks) {
      let t15;
      if ($[29] !== activeHref) {
        t15 = subLink => {
          const isActive = subLink.href === activeHref;
          return <Link key={subLink.href} href={subLink.href} className={cx("no-underline h-7 px-2 rounded-default flex items-center justify-between gap-1", "transition-transform duration-fast ease-out active:scale-pressed motion-reduce:active:scale-100", isActive ? "text-primary text-label-3 bg-senary/70" : "text-tertiary text-body-3 hover:text-primary hover:bg-hover")}><span className="truncate">{subLink.text}</span>{subLink.suffixElement}</Link>;
        };
        $[29] = activeHref;
        $[30] = t15;
      } else {
        t15 = $[30];
      }
      t14 = subLinks.map(t15);
      $[26] = activeHref;
      $[27] = subLinks;
      $[28] = t14;
    } else {
      t14 = $[28];
    }
    let t15;
    if ($[31] !== t14) {
      t15 = <div className="flex flex-col gap-0.5">{t14}</div>;
      $[31] = t14;
      $[32] = t15;
    } else {
      t15 = $[32];
    }
    let t16;
    if ($[33] !== t13 || $[34] !== t15) {
      t16 = <HoverCard.Portal><HoverCard.Content side="right" sideOffset={8} align="start" onPointerDownOutside={t12} className="z-50 min-w-[180px] p-1 bg-elevated border border-tertiary rounded shadow-md data-[state=open]:animate-fadeIn">{t13}{t15}</HoverCard.Content></HoverCard.Portal>;
      $[33] = t13;
      $[34] = t15;
      $[35] = t16;
    } else {
      t16 = $[35];
    }
    let t17;
    if ($[36] !== t11 || $[37] !== t16) {
      t17 = <HoverCard.Root openDelay={50} closeDelay={100}>{t11}{t16}</HoverCard.Root>;
      $[36] = t11;
      $[37] = t16;
      $[38] = t17;
    } else {
      t17 = $[38];
    }
    return t17;
  }
  let t7;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = cx("flex items-center gap-2 h-8 md:h-7 pl-1.5 pr-1 rounded-default w-full relative isolate text-tertiary text-body-3 hover:text-primary hover:bg-hover cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20", "transition-transform duration-fast ease-out active:scale-pressed motion-reduce:active:scale-100");
    $[39] = t7;
  } else {
    t7 = $[39];
  }
  let t8;
  if ($[40] !== Icon) {
    t8 = <Icon />;
    $[40] = Icon;
    $[41] = t8;
  } else {
    t8 = $[41];
  }
  let t9;
  if ($[42] !== text) {
    t9 = <span className="truncate text-left">{text}</span>;
    $[42] = text;
    $[43] = t9;
  } else {
    t9 = $[43];
  }
  let t10;
  if ($[44] !== annotationBadge || $[45] !== t9) {
    t10 = <div className="flex-1 gap-2 flex items-center min-w-0">{t9}{annotationBadge}</div>;
    $[44] = annotationBadge;
    $[45] = t9;
    $[46] = t10;
  } else {
    t10 = $[46];
  }
  const t11 = `transition-transform duration-200 ease-in-out select-none ${isOpen ? "rotate-90" : "rotate-0"}`;
  let t12;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <IcoChevronRight16 />;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  if ($[48] !== t11) {
    t13 = <div className={t11}>{t12}</div>;
    $[48] = t11;
    $[49] = t13;
  } else {
    t13 = $[49];
  }
  let t14;
  if ($[50] !== suffixElement || $[51] !== t13) {
    t14 = <div className="flex items-center">{suffixElement}{t13}</div>;
    $[50] = suffixElement;
    $[51] = t13;
    $[52] = t14;
  } else {
    t14 = $[52];
  }
  let t15;
  if ($[53] !== isOpen || $[54] !== t10 || $[55] !== t14 || $[56] !== t8) {
    t15 = <button type="button" onClick={handleToggle} aria-expanded={isOpen} className={t7}>{t8}{t10}{t14}</button>;
    $[53] = isOpen;
    $[54] = t10;
    $[55] = t14;
    $[56] = t8;
    $[57] = t15;
  } else {
    t15 = $[57];
  }
  let t16;
  if ($[58] !== activeHref || $[59] !== isOpen || $[60] !== subLinks) {
    t16 = isOpen && <motion.div className="flex flex-col gap-0.5 overflow-hidden" {...dropdownAnimation}>{subLinks.map(subLink_0 => <SubLink key={subLink_0.href} href={subLink_0.href} text={subLink_0.text} isActive={subLink_0.href === activeHref} suffixElement={subLink_0.suffixElement} />)}</motion.div>;
    $[58] = activeHref;
    $[59] = isOpen;
    $[60] = subLinks;
    $[61] = t16;
  } else {
    t16 = $[61];
  }
  let t17;
  if ($[62] !== t16) {
    t17 = <AnimatePresence initial={false}>{t16}</AnimatePresence>;
    $[62] = t16;
    $[63] = t17;
  } else {
    t17 = $[63];
  }
  let t18;
  if ($[64] !== t15 || $[65] !== t17) {
    t18 = <div className="flex flex-col gap-0.5">{t15}{t17}</div>;
    $[64] = t15;
    $[65] = t17;
    $[66] = t18;
  } else {
    t18 = $[66];
  }
  return t18;
};
export default memo(MenuDropdown);
function _temp(longest, cur) {
  return longest && longest.length >= cur.href.length ? longest : cur.href;
}
function _temp2(prev) {
  return !prev;
}